import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { invitePortal, sendClientInvite, sendInvite, sendPasswordReset } from "@/lib/adminUserAccess";
import { Copy, KeyRound, Loader2, MailPlus } from "lucide-react";


const COOLDOWN_MS = 30_000;

/** Disables a send button for 30 seconds after each send. */
export function useSendCooldown() {
  const [until, setUntil] = useState<number>(0);
  const [, force] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!until) return;
    timer.current = window.setInterval(() => force((n) => n + 1), 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [until]);

  const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));
  const start = useCallback(() => setUntil(Date.now() + COOLDOWN_MS), []);
  return { remaining, cooling: remaining > 0, start };
}

/** Sends a password reset email, with toast feedback and the invite fallback offer. */
export function useAccessActions() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const reset = useCallback(
    async (email: string, onStarted?: () => void) => {
      setBusy(true);
      try {
        const res = await sendPasswordReset(email);
        if (res.success) {
          onStarted?.();
          toast({
            title: "Password reset sent",
            description: res.message || `Password reset email sent to ${email}.`,
          });
        } else if (res.code === "no_account") {
          toast({
            title: "No account yet",
            description: res.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Could not send reset",
            description: res.message || "Please try again.",
            variant: "destructive",
          });
        }
        return res;
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  const invite = useCallback(
    async (email: string, fullName?: string, onStarted?: () => void) => {
      setBusy(true);
      try {
        const res = await sendInvite(email, fullName);
        if (res.success) {
          onStarted?.();
          toast({
            title: "Invitation sent",
            description:
              res.message ||
              `Invitation email sent to ${email}. They pick their own password from the email.`,
          });
        } else if (res.code === "already_exists") {
          toast({
            title: "Already has an account",
            description: `${email} already has an account. Would you like to send a password reset instead?`,
            action: (
              <ToastAction
                altText="Send password reset"
                onClick={() => {
                  void reset(email, onStarted);
                }}
              >
                Send reset
              </ToastAction>
            ),
          });
        } else {
          toast({
            title: "Could not send invitation",
            description: res.message || "Please try again.",
            variant: "destructive",
          });
        }
        return res;
      } finally {
        setBusy(false);
      }
    },
    [toast, reset],
  );

  return { invite, reset, busy };
}

export function SendResetButton({
  email,
  size = "sm",
  variant = "outline",
  label = "Send password reset",
}: {
  email: string;
  size?: "sm" | "default";
  variant?: "outline" | "ghost" | "default";
  label?: string;
}) {
  const { reset, busy } = useAccessActions();
  const { cooling, remaining, start } = useSendCooldown();

  return (
    <Button
      size={size}
      variant={variant}
      disabled={busy || cooling || !email}
      onClick={(e) => {
        e.stopPropagation();
        void reset(email, start);
      }}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <KeyRound className="h-4 w-4 mr-2" />
      )}
      {cooling ? `Wait ${remaining}s` : label}
    </Button>
  );
}

export function SendInviteDialog({
  open,
  onOpenChange,
  defaultEmail = "",
  defaultName = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  defaultName?: string;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const { invite, busy } = useAccessActions();
  const { cooling, remaining, start } = useSendCooldown();

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail);
      setName(defaultName);
    }
  }, [open, defaultEmail, defaultName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send an invitation</DialogTitle>
          <DialogDescription>
            We email an invitation. The person picks their own password. You never
            see or set it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="invite-name">Full name (optional)</Label>
            <Input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Steward"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy || cooling || !email.trim()}
            onClick={async () => {
              const res = await invite(email.trim(), name.trim(), start);
              if (res.success) onOpenChange(false);
            }}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MailPlus className="h-4 w-4 mr-2" />
            )}
            {cooling ? `Wait ${remaining}s` : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Sends an existing member their way in to the portal. */
export function InviteToPortalButton({
  memberRecordId,
  size = "sm",
  variant = "outline",
  label = "Invite to portal",
  onSent,
}: {
  memberRecordId: string;
  size?: "sm" | "default";
  variant?: "outline" | "ghost" | "default";
  label?: string;
  onSent?: () => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const { cooling, remaining, start } = useSendCooldown();

  const send = async () => {
    setBusy(true);
    try {
      const res = await invitePortal(memberRecordId);
      if (res.success) {
        start();
        onSent?.();
        toast({
          title: res.created ? "Portal welcome sent" : "Sign in link sent",
          description: res.message,
        });
      } else {
        toast({
          title: "Could not send the invitation",
          description: res.message || "Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size={size}
      variant={variant}
      disabled={busy || cooling || !memberRecordId}
      onClick={(e) => {
        e.stopPropagation();
        void send();
      }}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <MailPlus className="h-4 w-4 mr-2" />
      )}
      {cooling ? `Wait ${remaining}s` : label}
    </Button>
  );
}

/** Mints the welcome link for a new client and emails it to them. */
export function NewClientInviteDialog({
  open,
  onOpenChange,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [unaAmount, setUnaAmount] = useState("");
  const [monthlyAmount, setMonthlyAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [mintedUrl, setMintedUrl] = useState("");
  const { cooling, remaining, start } = useSendCooldown();

  useEffect(() => {
    if (open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setUnaAmount("");
      setMonthlyAmount("");
      setMintedUrl("");
    }
  }, [open]);

  const send = async () => {
    setBusy(true);
    try {
      const res = await sendClientInvite({
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        una_amount: unaAmount.trim() || undefined,
        monthly_amount: monthlyAmount.trim() || undefined,
      });
      if (res.success) {
        start();
        setMintedUrl(res.url || "");
        onSent?.();
        toast({ title: res.sent ? "Invitation sent" : "Link ready", description: res.message });
      } else {
        toast({
          title: "Could not send the invitation",
          description: res.message || "Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New client invite</DialogTitle>
          <DialogDescription>
            We email a private welcome link. They choose their own password when
            they open it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="client-invite-email">Email</Label>
            <Input
              id="client-invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="client-invite-first">First name</Label>
              <Input
                id="client-invite-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-invite-last">Last name</Label>
              <Input
                id="client-invite-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Steward"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="client-invite-una">Association amount</Label>
              <Input
                id="client-invite-una"
                value={unaAmount}
                onChange={(e) => setUnaAmount(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-invite-monthly">Monthly amount</Label>
              <Input
                id="client-invite-monthly"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {mintedUrl && (
          <div className="rounded-md border bg-muted/40 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Their welcome link, in case you want to share it another way.
            </p>
            <p className="text-xs break-all font-mono">{mintedUrl}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(mintedUrl);
                toast({ title: "Link copied" });
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy link
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {mintedUrl ? "Close" : "Cancel"}
          </Button>
          <Button disabled={busy || cooling || !email.trim()} onClick={() => void send()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MailPlus className="h-4 w-4 mr-2" />
            )}
            {cooling ? `Wait ${remaining}s` : mintedUrl ? "Send again" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
